/* eslint-disable no-console */
import { useState, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { Button, Layout, Menu, Typography } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  NotificationFilled,
  StarFilled
} from '@ant-design/icons'

import EALogo from 'Assets/easyassist-logo.png'
import EALogoCollapsed from 'Assets/ea-logo.png'

import SubmenuPanel from './SubmenuPanel'
import menuItems from './paths'
import './Sidebar.less'

const { Sider } = Layout
const { Text } = Typography

const findMenuItemByKey = (key) => {
  let found = null
  let foundParent = null
  let foundGrandParent = null

  menuItems.forEach((menuItem) => {
    if (menuItem.key === key) {
      found = menuItem
      return
    }
    if (menuItem.subitems) {
      menuItem.subitems.forEach((subitem) => {
        if (subitem.key === key) {
          found = subitem
          foundParent = menuItem
          return
        }
        if (subitem.subitems) {
          subitem.subitems.forEach((nestedItem) => {
            if (nestedItem.key === key) {
              found = nestedItem
              foundParent = subitem
              foundGrandParent = menuItem
            }
          })
        }
      })
    }
  })

  return { found, foundParent, foundGrandParent }
}

const loadFavoritesFromLocalStorage = (user) => {
  const storedFavorites = localStorage.getItem(`favorites_${user}`)
  if (!storedFavorites) return []

  try {
    const favorites = JSON.parse(storedFavorites)

    // Check if we need to migrate old format
    const needsMigration = favorites.some(
      (fav) => !fav.path || fav.icon // If has icon or missing path, needs migration
    )

    if (needsMigration) {
      const migratedFavorites = favorites
        .map((fav) => {
          // If it's already in the new format (has path and no icon), keep it
          if (fav.path && !fav.icon) {
            return fav
          }

          // Find the menu item and its parents
          const { found, foundParent, foundGrandParent } = findMenuItemByKey(
            fav.key
          )

          // If we can't find the item in the current menu structure, try to preserve the old data
          if (!found) {
            const { key, label, parentLabel = null } = fav
            // If the old format has a path in the key, use it
            return {
              key,
              label,
              path: key, // In the old format, key was the path
              parentLabel
            }
          }

          // Construct the parent label based on the menu structure or keep existing
          const { parentLabel: existingParentLabel } = fav
          let parentLabel = existingParentLabel // Keep existing parentLabel if present
          if (!parentLabel) {
            if (foundGrandParent) {
              parentLabel = `${foundGrandParent.label} > ${foundParent.label}`
            } else if (foundParent) {
              parentLabel = foundParent.label
            }
          }

          return {
            key: found.key,
            label: found.label,
            path: found.path || fav.key, // Use found path or fallback to key
            parentLabel
          }
        })
        .filter(Boolean) // Remove null entries

      // Save migrated favorites back to localStorage
      localStorage.setItem(
        `favorites_${user}`,
        JSON.stringify(migratedFavorites)
      )
      return migratedFavorites
    }

    return favorites
  } catch (error) {
    console.error('Error loading favorites:', error)
    return []
  }
}

const MenuItemContent = ({ label, parentLabel }) => (
  <Text ellipsis style={{ maxWidth: '100%', color: '#fff' }}>
    {parentLabel ? `${parentLabel} > ${label}` : label}
  </Text>
)

MenuItemContent.propTypes = {
  label: PropTypes.string.isRequired,
  parentLabel: PropTypes.string
}

MenuItemContent.defaultProps = {
  parentLabel: null
}

const Sidebar = forwardRef(
  (
    {
      activeSubmenu,
      collapsed,
      mobileMenuVisible,
      novidadesData,
      onMobileClose,
      setActiveSubmenu,
      setCollapsed
    },
    ref
  ) => {
    const [triggerElement, setTriggerElement] = useState(null)
    const [activeMenuItem, setActiveMenuItem] = useState(null)
    const navigate = useNavigate()
    const usuario =
      localStorage.getItem('earetaguarda_usuario_login') || 'defaultUser'
    const [favorites, setFavorites] = useState(
      loadFavoritesFromLocalStorage(usuario)
    )

    useEffect(() => {
      if (mobileMenuVisible && window.innerWidth <= 767) {
        const handleItemClick = () => {
          onMobileClose()
        }

        const navItems = document.querySelectorAll('.ant-menu-item')
        navItems.forEach((item) => {
          item.addEventListener('click', handleItemClick)
        })

        return () => {
          navItems.forEach((item) => {
            item.removeEventListener('click', handleItemClick)
          })
        }
      }
      return undefined
    }, [mobileMenuVisible, onMobileClose])

    useEffect(() => {
      const savedCollapsed = localStorage.getItem('sidebar_collapsed')
      if (savedCollapsed !== null) {
        setCollapsed(savedCollapsed === 'true')
      }
    }, [setCollapsed])

    useEffect(() => {
      localStorage.setItem('sidebar_collapsed', collapsed.toString())
    }, [collapsed])

    useEffect(() => {
      setFavorites(loadFavoritesFromLocalStorage(usuario))
    }, [usuario])

    // Update submenu data when favorites change
    useEffect(() => {
      if (activeSubmenu && activeSubmenu[0]?.title === 'Meu Menu') {
        const submenuData = [
          {
            title: 'Meu Menu',
            items: favorites.map((fav) => ({
              key: fav.key,
              label: fav.label,
              path: fav.path,
              parentLabel: fav.parentLabel
            }))
          }
        ]
        setActiveSubmenu(submenuData)
      }
    }, [favorites])

    // Update activeMenuItem when submenu panel is closed
    useEffect(() => {
      if (!activeSubmenu) {
        setActiveMenuItem(null)
      }
    }, [activeSubmenu])

    const handleMenuClick = (item, event) => {
      // Get the clicked menu item element
      const element =
        event.target.closest('.ant-menu-item') ||
        event.target.closest('.ant-menu-submenu-title')

      if (activeSubmenu && activeSubmenu[0]?.title === item.label) {
        setActiveSubmenu(null)
        setTriggerElement(null)
        setActiveMenuItem(null)
        return
      }

      if (item.key === 'favorites') {
        const submenuData = [
          {
            title: 'Meu Menu',
            items: favorites.map((fav) => ({
              key: fav.key,
              label: fav.label,
              path: fav.path,
              parentLabel: fav.parentLabel,
              permission: fav.permission
            }))
          }
        ]
        setActiveSubmenu(submenuData)
        setTriggerElement(element)
        setActiveMenuItem('favorites')
      } else if (item.path && typeof item.path === 'string') {
        navigate(item.path)
        setActiveSubmenu(null)
        setTriggerElement(null)
        setActiveMenuItem(null)
        setCollapsed(true)
      } else if (item.subitems) {
        const submenuData = [
          {
            title: item.label,
            items: item.subitems.map((subitem) => ({
              key: subitem.key,
              label: subitem.label,
              path: subitem.path,
              subitems: subitem.subitems,
              permission: subitem.permission
            }))
          }
        ]
        setActiveSubmenu(submenuData)
        setTriggerElement(element)
        setActiveMenuItem(item.key)
      }
    }

    const renderMenuItems = (items) =>
      items.map((item) => ({
        key: item.key,
        icon: item.icon,
        label: <MenuItemContent label={item.label} />,
        className: activeMenuItem === item.key ? 'menu-item-active' : '',
        onClick: (e) => {
          if (!item.subitems && !item.path) {
            console.warn(`Menu item "${item.label}" has no path or subitems`)
            return
          }
          handleMenuClick(item, e.domEvent)
        }
      }))

    const favoritesMenuItem =
      favorites.length > 0
        ? [
            {
              key: 'favorites',
              icon: <StarFilled />,
              label: <MenuItemContent label="Meu Menu" />,
              className:
                activeMenuItem === 'favorites' ? 'menu-item-active' : '',
              onClick: (e) =>
                handleMenuClick(
                  { key: 'favorites', label: 'Meu Menu' },
                  e.domEvent
                )
            }
          ]
        : []

    const novidadesMenuItem =
      novidadesData && novidadesData.count > 0
        ? [
            {
              key: 'novidade',
              icon: <NotificationFilled />,
              label: 'Novidades',

              onClick: () => navigate('/novidade')
            }
          ]
        : []

    return (
      <>
        <Sider
          ref={ref}
          trigger={null}
          collapsible
          className={`app__sidebar ${collapsed ? 'collapsed' : ''} ${
            mobileMenuVisible ? 'mobile-visible' : ''
          }`}
          width={300}
          collapsed={collapsed}
        >
          <div
            className="app__sidebar__logo"
            style={{
              backgroundColor: '#fff',
              padding: '8px',
              position: 'relative',
              height: '64px',
              width: '100%'
            }}
          >
            <img
              src={EALogo}
              alt="Easy@assist logo"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) scale(${
                  collapsed ? 0.8 : 1
                })`,
                opacity: collapsed ? 0 : 1,
                transition: 'all 0.1s ease',
                maxWidth: 'calc(100% - 16px)',
                maxHeight: '48px',
                objectFit: 'contain'
              }}
            />
            <img
              src={EALogoCollapsed}
              alt="Easy@assist logo collapsed"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) scale(${
                  collapsed ? 1 : 0.8
                })`,
                opacity: collapsed ? 1 : 0,
                transition: 'all 0.1s ease',
                maxWidth: '40px',
                maxHeight: '64px',
                objectFit: 'contain'
              }}
            />
          </div>
          <Menu
            theme="dark"
            mode="inline"
            defaultSelectedKeys={['dashboard']}
            items={[
              ...favoritesMenuItem,
              ...novidadesMenuItem,
              ...renderMenuItems(menuItems)
            ]}
          />
          <Button
            type="text"
            icon={collapsed ? <CaretRightOutlined /> : <CaretLeftOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              color: '#fff',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              margin: '0 auto',
              width: '100%'
            }}
          />
        </Sider>
        <SubmenuPanel
          visible={!!activeSubmenu}
          submenus={activeSubmenu}
          onClose={() => {
            setActiveSubmenu(null)
            setTriggerElement(null)
            setActiveMenuItem(null)
          }}
          onFavoriteChange={(newFavorites) => setFavorites(newFavorites)}
          favorites={favorites}
          triggerElement={triggerElement}
          setCollapsed={setCollapsed}
        />
      </>
    )
  }
)

Sidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  setCollapsed: PropTypes.func.isRequired,
  activeSubmenu: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          path: PropTypes.string,
          permission: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.arrayOf(PropTypes.number)
          ]),
          subitems: PropTypes.arrayOf(
            PropTypes.shape({
              key: PropTypes.string.isRequired,
              label: PropTypes.string.isRequired,
              path: PropTypes.string,
              permission: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.arrayOf(PropTypes.number)
              ])
            })
          )
        })
      ).isRequired
    })
  ),
  setActiveSubmenu: PropTypes.func.isRequired,
  novidadesData: PropTypes.shape({
    count: PropTypes.number,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        assunto: PropTypes.string,
        mensagem: PropTypes.string,
        tipo: PropTypes.number
      })
    )
  }),
  mobileMenuVisible: PropTypes.bool,
  onMobileClose: PropTypes.func
}

Sidebar.defaultProps = {
  activeSubmenu: null,
  novidadesData: [],
  mobileMenuVisible: false,
  onMobileClose: () => {}
}

Sidebar.displayName = 'Sidebar'

export default Sidebar
